import { useEffect, useState, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useToast } from "@cynaps/ui";
import { SpecialtySelection, AnnotationTestTask, TestResultsDisplay } from "./components";
import type { SelectedExpertise } from "./components/SpecialtySelection";
import { getTestCasesForSpecialties, getEstimatedTime, type TestCase, type AnnotationResult } from "./data/testCases";
import { calculateTestResults, type TestResult } from "./data/scoring";
import "./AnnotatorSkillTest.css";

type TestPhase = "specialty-selection" | "instructions" | "testing" | "results";

export const AnnotatorSkillTest = () => {
  const history = useHistory();
  const toast = useToast();

  // Test state
  const [phase, setPhase] = useState<TestPhase>("specialty-selection");
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [selectedExpertise, setSelectedExpertise] = useState<SelectedExpertise[]>([]);
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [answers, setAnswers] = useState<Map<string, AnnotationResult[]>>(new Map());
  const answersRef = useRef<Map<string, AnnotationResult[]>>(new Map());
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [earnedBadges, setEarnedBadges] = useState<
    Array<{
      name: string;
      category: string;
      specialization?: string | null;
      earned_at: string;
    }>
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  // Timer
  const [timeRemaining, setTimeRemaining] = useState(0);
  const startTimeRef = useRef<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch("/api/annotators/auth-check", {
          credentials: "include",
        });
        const data = await res.json();

        if (!data.authenticated) {
          history.push("/annotators/login");
        }
      } catch (error) {
        console.error("Auth check failed:", error);
      }
    };

    checkAuth();
  }, [history]);

  // Timer effect
  useEffect(() => {
    if (phase === "testing" && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            // Time's up - auto submit
            handleFinishTest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    }
  }, [phase, timeRemaining]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Handle specialty selection confirmation
  const handleSpecialtyConfirm = useCallback(async (specialties: string[], expertiseData: SelectedExpertise[]) => {
    setSelectedSpecialties(specialties);
    setSelectedExpertise(expertiseData);
    setIsLoading(true);

    try {
      const allCases: TestCase[] = [];

      // Step 1: GET the dataset catalog to discover available manifests
      const catalogRes = await fetch("/api/annotators/expertise/debug-test-samples", {
        credentials: "include",
      });

      if (catalogRes.ok) {
        const catalog = await catalogRes.json();
        const categories: any[] = catalog.categories || [];

        // Step 2: For each selected specialty, find matching manifest specializations
        for (const selectedSlug of specialties) {
          // Collect all specialization slugs that belong to the selected category
          const matchingSlugs: string[] = [];

          for (const cat of categories) {
            if (cat.slug === selectedSlug) {
              // Direct category match — use its specializations
              for (const spec of cat.specializations || []) {
                matchingSlugs.push(spec.slug);
              }
              // If no specializations, use the category slug itself
              if (matchingSlugs.length === 0) {
                matchingSlugs.push(cat.slug);
              }
              break;
            }
            // Check if selectedSlug matches a specialization directly
            for (const spec of cat.specializations || []) {
              if (spec.slug === selectedSlug) {
                matchingSlugs.push(spec.slug);
              }
            }
          }

          // If no matches found in catalog, try the raw slug anyway
          if (matchingSlugs.length === 0) {
            matchingSlugs.push(selectedSlug);
          }

          // Step 3: POST for each matched specialization slug (limit to 2 per category)
          const slugsToFetch = matchingSlugs.slice(0, 2);
          for (const specSlug of slugsToFetch) {
            try {
              const response = await fetch("/api/annotators/expertise/debug-test-samples", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ specialization_slug: specSlug, max_tasks: 5 }),
              });

              if (response.ok) {
                const data = await response.json();
                const tasks = data.tasks || [];

                tasks.forEach((task: any, i: number) => {
                  allCases.push({
                    id: `api-${specSlug}-${i}`,
                    specialty: selectedSlug,
                    title: task.project_title || data.specialization || specSlug.replace(/-/g, " "),
                    description: `Annotation task: ${task.project_title || specSlug}`,
                    difficulty: task.difficulty || "medium",
                    points: task.points || 10,
                    config: task.label_config || "",
                    task: {
                      data: task.data || {},
                      predictions: task.predictions || [],
                    },
                    groundTruth: task.ground_truth ? [task.ground_truth] : [],
                    scoringCriteria: { type: task.has_ground_truth ? "classification" : "exact" },
                  });
                });
              }
            } catch (err) {
              console.warn(`Failed to fetch test data for ${specSlug}:`, err);
            }
          }
        }
      }

      // Fall back to hardcoded tests only if the API returned nothing
      const cases = allCases.length > 0 ? allCases : getTestCasesForSpecialties(specialties);
      setTestCases(cases);

      // Calculate time (estimated time in minutes, convert to seconds)
      const estimatedMinutes = getEstimatedTime(cases);
      setTimeRemaining(estimatedMinutes * 60);

      setPhase("instructions");
    } catch (error) {
      console.warn("API fetch failed, using hardcoded test cases:", error);
      const cases = getTestCasesForSpecialties(specialties);
      setTestCases(cases);
      const estimatedMinutes = getEstimatedTime(cases);
      setTimeRemaining(estimatedMinutes * 60);
      setPhase("instructions");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Start the test
  const handleStartTest = useCallback(async () => {
    setIsLoading(true);

    try {
      // Optionally notify backend that test is starting
      const response = await fetch("/api/annotators/test/start", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          specialties: selectedSpecialties,
          testCaseIds: testCases.map((tc) => tc.id),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to start test");
      }

      const data = await response.json();
      if (data.attempt_id) {
        setAttemptId(data.attempt_id);
      }

      startTimeRef.current = Date.now();
      setPhase("testing");
    } catch (error) {
      // If API fails, still allow local testing
      console.warn("Could not notify server of test start:", error);
      startTimeRef.current = Date.now();
      setPhase("testing");
    } finally {
      setIsLoading(false);
    }
  }, [selectedSpecialties, testCases]);

  // Finish test and calculate results
  const handleFinishTest = useCallback(
    async (finalAnswers?: Map<string, AnnotationResult[]>) => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Use answers from ref to ensure we have everything including the last one
      const timeTaken = Math.floor((Date.now() - startTimeRef.current) / 1000);

      console.warn("[AnnotatorTest] (v2.1) Calculating results. Answers:", Array.from(answersRef.current.entries()));
      console.log(testCases, answersRef.current, timeTaken, finalAnswers);
      const result = calculateTestResults(testCases, answersRef.current, timeTaken);
      setTestResult(result);

      // Submit results to backend
      try {
        const response = await fetch("/api/annotators/test/submit", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            attempt_id: attemptId,
            specialties: selectedSpecialties,
            expertise: selectedExpertise.map((e) => ({
              category_id: e.categoryId,
              category_slug: e.categorySlug,
              specialization_id: e.specializationId || null,
              specialization_slug: e.specializationSlug || null,
            })),
            results: {
              ...result,
              taskScores: result.taskScores.map((ts) => ({
                testCaseId: ts.testCaseId,
                specialty: ts.specialty,
                earnedPoints: ts.earnedPoints,
                maxPoints: ts.maxPoints,
                percentage: ts.percentage,
              })),
            },
          }),
        });

        if (response.ok) {
          const data = await response.json();
          // Save earned badges from the response
          if (data.earned_badges && Array.isArray(data.earned_badges)) {
            setEarnedBadges(data.earned_badges);
          }
        }
      } catch (error) {
        console.warn("Could not submit results to server:", error);
      }

      setPhase("results");
    },
    [testCases, selectedSpecialties, selectedExpertise, attemptId],
  );

  // Handle task completion
  const handleTaskComplete = useCallback(
    (annotation: AnnotationResult[]) => {
      const currentTestCase = testCases[currentTaskIndex];

      setAnswers((prev) => {
        const newAnswers = new Map(prev);
        newAnswers.set(currentTestCase.id, annotation);
        return newAnswers;
      });

      console.warn(`[AnnotatorTest] (v2.1) Task ${currentTestCase.id} complete. Annotation:`, annotation);

      // Update ref guarantees fresh state for submission
      answersRef.current.set(currentTestCase.id, annotation);

      // Move to next task or finish
      if (currentTaskIndex < testCases.length - 1) {
        setCurrentTaskIndex((prev) => prev + 1);
      } else {
        console.warn("[AnnotatorTest] (v2.1) Finishing test with answers:", Array.from(answersRef.current.entries()));
        handleFinishTest();
      }
    },
    [testCases, currentTaskIndex, handleFinishTest],
  );

  // Handle task skip
  const handleTaskSkip = useCallback(() => {
    if (currentTaskIndex < testCases.length - 1) {
      setCurrentTaskIndex((prev) => prev + 1);
    } else {
      handleFinishTest();
    }
  }, [testCases.length, currentTaskIndex]);

  // Handle retry
  const handleRetry = useCallback(() => {
    setPhase("specialty-selection");
    setSelectedSpecialties([]);
    setSelectedExpertise([]);
    setTestCases([]);
    setCurrentTaskIndex(0);
    setAnswers(new Map());
    answersRef.current = new Map();
    setAttemptId(null);
    setTestResult(null);
    setEarnedBadges([]);
    setTimeRemaining(0);
  }, []);

  // Handle continue to dashboard - redirect through login to establish session
  const handleContinue = useCallback(() => {
    // Redirect to login which will then redirect to earnings dashboard
    // This ensures proper Django session authentication
    window.location.href = "/login/?next=/projects/";
  }, []);

  // Render based on phase
  const renderPhase = () => {
    switch (phase) {
      case "specialty-selection":
        return <SpecialtySelection onConfirm={handleSpecialtyConfirm} isLoading={isLoading} />;

      case "instructions":
        return (
          <div className="test-instructions">
            <div className="test-instructions__content">
              <div className="test-instructions__number">
                02/ <small style={{ fontSize: "10px", opacity: 0.5 }}>v2.1</small>
              </div>
              <h1 className="test-instructions__title">Test Instructions</h1>

              <div className="test-instructions__summary">
                <div className="test-instructions__summary-item">
                  <span className="test-instructions__summary-label">Specialties</span>
                  <span className="test-instructions__summary-value">{selectedSpecialties.length} selected</span>
                </div>
                <div className="test-instructions__summary-item">
                  <span className="test-instructions__summary-label">Tasks</span>
                  <span className="test-instructions__summary-value">{testCases.length} total</span>
                </div>
                <div className="test-instructions__summary-item">
                  <span className="test-instructions__summary-label">Time Limit</span>
                  <span className="test-instructions__summary-value">{Math.ceil(timeRemaining / 60)} minutes</span>
                </div>
              </div>

              <div className="test-instructions__rules">
                <h3>Before you begin:</h3>
                <ul>
                  <li>
                    <span className="test-instructions__rule-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </span>
                    The timer starts once you click "Begin Test" and cannot be paused.
                  </li>
                  <li>
                    <span className="test-instructions__rule-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                      </svg>
                    </span>
                    Read each task carefully before annotating. Follow the instructions provided.
                  </li>
                  <li>
                    <span className="test-instructions__rule-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    </span>
                    You must complete an annotation before submitting each task.
                  </li>
                  <li>
                    <span className="test-instructions__rule-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <polygon points="5 4 15 12 5 20 5 4" />
                        <line x1="19" y1="5" x2="19" y2="19" />
                      </svg>
                    </span>
                    You can skip tasks, but skipped tasks will receive 0 points.
                  </li>
                  <li>
                    <span className="test-instructions__rule-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="12" cy="12" r="10" />
                        <circle cx="12" cy="12" r="6" />
                        <circle cx="12" cy="12" r="2" />
                      </svg>
                    </span>
                    You need at least 70% to pass. Try your best on each task!
                  </li>
                </ul>
              </div>

              <div className="test-instructions__actions">
                <button
                  type="button"
                  className="test-instructions__back"
                  onClick={() => setPhase("specialty-selection")}
                >
                  ← Back
                </button>
                <button
                  type="button"
                  className="test-instructions__start"
                  onClick={handleStartTest}
                  disabled={isLoading}
                >
                  {isLoading ? "Starting..." : "Begin Test"}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        );

      case "testing":
        const currentTestCase = testCases[currentTaskIndex];
        return (
          <AnnotationTestTask
            key={currentTestCase.id}
            testCase={currentTestCase}
            onComplete={handleTaskComplete}
            onSkip={handleTaskSkip}
            taskNumber={currentTaskIndex + 1}
            totalTasks={testCases.length}
            timeRemaining={timeRemaining}
          />
        );

      case "results":
        return testResult ? (
          <TestResultsDisplay
            result={testResult}
            earnedBadges={earnedBadges}
            onRetry={handleRetry}
            onContinue={handleContinue}
          />
        ) : null;

      default:
        return null;
    }
  };

  return <div className="annotator-skill-test">{renderPhase()}</div>;
};

// Export for router
export default AnnotatorSkillTest;
